import React, { useMemo, useState } from 'react';
import './LessonQuiz.css';

function normalizeQuiz(quiz) {
  if (!quiz || typeof quiz !== 'object') return null;
  const questions = Array.isArray(quiz.questions) ? quiz.questions : [];
  if (questions.length === 0) return null;
  const normalized = questions.map((q, idx) => ({
    id: q.id || `q-${idx}`,
    prompt: String(q.prompt || ''),
    options: Array.isArray(q.options) ? q.options.map((o) => String(o ?? '')) : [],
    correctIndex: Number.isFinite(Number(q.correctIndex)) ? Number(q.correctIndex) : 0,
    explanation: q.explanation ? String(q.explanation) : '',
  }));
  return { enabled: quiz.enabled !== false, questions: normalized };
}

function storageKey({ userId, courseId, lessonId }) {
  return `infobloom.quiz:${userId || 'anon'}:${courseId}:${lessonId}`;
}

export default function LessonQuiz({ quiz, userId, courseId, lessonId }) {
  const q = useMemo(() => normalizeQuiz(quiz), [quiz]);
  const [selected, setSelected] = useState(() => ({}));
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(null);

  if (!q || !q.enabled) return null;

  const questions = q.questions;

  const total = questions.length;
  const answered = Object.keys(selected).length;

  const handleSubmit = () => {
    const correct = questions.reduce((acc, item, idx) => {
      const pick = selected[idx];
      return acc + (Number(pick) === Number(item.correctIndex) ? 1 : 0);
    }, 0);
    setSubmitted(true);
    setScore({ correct, total });
    try {
      localStorage.setItem(
        storageKey({ userId, courseId, lessonId }),
        JSON.stringify({
          at: Date.now(),
          correct,
          total,
          selected,
        })
      );
    } catch {
      // ignore
    }
  };

  const handleReset = () => {
    setSelected({});
    setSubmitted(false);
    setScore(null);
    try {
      localStorage.removeItem(storageKey({ userId, courseId, lessonId }));
    } catch {
      // ignore
    }
  };

  return (
    <section className="lesson-quiz" aria-label="Lesson quiz">
      <div className="lesson-quiz-head">
        <div>
          <h2 className="lesson-quiz-title">Quiz</h2>
          <p className="lesson-quiz-sub">
            {total} question{total !== 1 ? 's' : ''}{submitted ? ' · Submitted' : ` · Answered ${answered}/${total}`}
          </p>
        </div>
        <div className="lesson-quiz-actions">
          {!submitted ? (
            <button
              type="button"
              className="lesson-quiz-btn"
              onClick={handleSubmit}
              disabled={answered < total}
              title={answered < total ? 'Answer all questions to submit' : 'Submit quiz'}
            >
              Submit
            </button>
          ) : (
            <button type="button" className="lesson-quiz-btn lesson-quiz-btn-outline" onClick={handleReset}>
              Try again
            </button>
          )}
        </div>
      </div>

      {submitted && score && (
        <div className="lesson-quiz-score" role="status">
          Score: <strong>{score.correct}/{score.total}</strong>
        </div>
      )}

      <ol className="lesson-quiz-list">
        {questions.map((item, idx) => {
          const pick = selected[idx];
          const correctIdx = item.correctIndex;
          const isCorrect = submitted && Number(pick) === Number(correctIdx);
          const isWrong = submitted && pick != null && Number(pick) !== Number(correctIdx);

          return (
            <li key={item.id} className="lesson-quiz-item">
              <p className="lesson-quiz-prompt">{item.prompt || `Question ${idx + 1}`}</p>
              <div className="lesson-quiz-options" role="radiogroup" aria-label={`Question ${idx + 1}`}>
                {item.options.slice(0, 4).map((opt, optIdx) => {
                  const checked = Number(pick) === optIdx;
                  const showCorrect = submitted && optIdx === correctIdx;
                  const showWrong = submitted && checked && optIdx !== correctIdx;
                  return (
                    <label
                      key={optIdx}
                      className={`lesson-quiz-option ${checked ? 'checked' : ''} ${showCorrect ? 'correct' : ''} ${showWrong ? 'wrong' : ''}`}
                    >
                      <input
                        type="radio"
                        name={`quiz-${courseId}-${lessonId}-${idx}`}
                        checked={checked}
                        disabled={submitted}
                        onChange={() => setSelected((prev) => ({ ...prev, [idx]: optIdx }))}
                      />
                      <span className="lesson-quiz-option-text">{opt || `Option ${optIdx + 1}`}</span>
                    </label>
                  );
                })}
              </div>
              {submitted && item.explanation && (
                <p className="lesson-quiz-explanation">
                  <strong>{isCorrect ? 'Correct:' : isWrong ? 'Review:' : 'Explanation:'}</strong> {item.explanation}
                </p>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

